import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Check, ArrowRight, Sparkles, Loader2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PqcAuditSuite from "../components/pricing/PqcAuditSuite";
import { useUserAuth } from "../lib/userAuth";
import useExperiment from "../lib/useExperiment";

import useDocumentTitle from "../hooks/useDocumentTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: "free",
    label: "Free",
    price: "$0",
    cadence: "forever",
    blurb: "Try the audit engine on your own contract — no signup needed.",
    cta: "Run a free preview",
    ctaTo: "/audit/preview",
    ctaKind: "link",
    accent: "#5D7394",
    features: [
      "1 free preview audit per session",
      "Same QorTrace audit engine",
      "Severity-tiered findings + scores",
      "No persistence, no certificate",
    ],
    footnote: "No credit card.",
  },
  {
    id: "oneshot",
    label: "One-Shot",
    price: "$149",
    cadence: "one-time",
    blurb: "For the team that just needs one contract audited and delivered.",
    cta: "Buy One-Shot — $149",
    ctaSku: "audit_oneshot",
    ctaKind: "checkout",
    accent: "#1FDD83",
    features: [
      "1 Standard audit, full deliverable",
      "Public certificate (HTML / PNG / SVG)",
      "Multi-page PDF report",
      "Findings tracker + re-score on fixes",
      "Shareable badges for README + docs",
    ],
    footnote: "Single audit, single project.",
  },
  {
    id: "builder",
    label: "Builder",
    price: "$99",
    cadence: "/ month",
    annual: "$990 / yr · save 17%",
    highlight: true,
    badge: "MOST POPULAR",
    blurb: "Active dev releasing new contracts every sprint.",
    cta: "Start Builder — $99/mo",
    ctaSku: "audit_builder_monthly",
    ctaSkuAnnual: "audit_builder_annual",
    ctaKind: "checkout",
    accent: "#00E5FF",
    features: [
      "3 Standard audits / month",
      "PQC scanner monitoring (5 wallets)",
      "All One-Shot deliverables",
      "Cert co-branding (your logo)",
      "Priority audit queue",
      "$149 / additional audit (if you blow the cap)",
    ],
    footnote: "Cancel anytime · monthly or annual.",
  },
  {
    id: "team",
    label: "Team",
    price: "$199",
    cadence: "/ month",
    annual: "$1,990 / yr · save 17%",
    blurb: "Multi-engineer team auditing every PR + monitoring deploys.",
    cta: "Start Team — $199/mo",
    ctaSku: "audit_team_monthly",
    ctaSkuAnnual: "audit_team_annual",
    ctaKind: "checkout",
    accent: "#FFB000",
    features: [
      "15 Standard audits / month",
      "PQC monitoring (50 wallets)",
      "Multi-seat (up to 10 engineers)",
      "10% off Deep Dive engagements",
      "Dedicated cert co-branding",
      "Priority email support",
    ],
    footnote: "Cancel anytime · monthly or annual.",
  },
  {
    id: "enterprise",
    label: "Enterprise",
    price: "Custom",
    cadence: "",
    blurb: "Protocols, exchanges, and large-scale teams with compliance needs.",
    cta: "Talk to sales",
    ctaTo: "/audit/talk-to-sales",
    ctaKind: "link",
    accent: "#FF8000",
    features: [
      "Unlimited Standard audits",
      "Negotiated Deep Dive engagements",
      "SSO + audit log + role-based access",
      "Dedicated auditor pool",
      "SLA + private Slack/Discord support",
      "On-prem option available",
    ],
    footnote: "Quoted per engagement.",
  },
];

const DEEP_DIVE = {
  label: "Deep Dive Audit",
  price: "from $4,999",
  blurb:
    "Senior auditor reviews and edits the AI's findings before delivery. Scoped per engagement based on LOC, chain, and protocol complexity.",
  features: [
    "Human-reviewed findings + recommendations",
    "Threat-model walkthrough (1-2 hour call)",
    "Fix-validation re-audit included",
    "Confidentiality NDA standard",
    "10% discount for Team + Enterprise members",
  ],
};

// ---------------------------------------------------------------------------
// Default export — standalone /pricing/audits page (Header + Footer chrome).
// Internally just renders <PricingAuditsContent /> so the same content can be
// embedded inside /contract (iter46) without page chrome.
// ---------------------------------------------------------------------------
export default function PricingAudits() {
  useDocumentTitle('Audit Pricing — QorTrace');
  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <main>
        <PricingAuditsContent embedded={false} />
      </main>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Embeddable variant — same content WITHOUT global chrome. Used to mount the
// audit pricing block inside /contract so devs see analyzer + audit upgrade
// in one unified flow.
// ---------------------------------------------------------------------------
export function PricingAuditsContent({ embedded = false }) {
  const navigate = useNavigate();
  const { account, loading: authLoading } = useUserAuth();
  const [searchParams] = useSearchParams();
  // Iter49 — honor `?cycle=annual` from /account/usage upsell links so the
  // toggle defaults to whatever the upstream page intended.
  const [annual, setAnnual] = useState(searchParams.get("cycle") === "annual");
  const [pendingSku, setPendingSku] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Re-sync if the URL ?cycle changes during the session (e.g. user navigates
  // between two upsell CTAs, one annual + one monthly).
  useEffect(() => {
    setAnnual(searchParams.get("cycle") === "annual");
  }, [searchParams]);

  // Hoisted A/B/N experiment — shared by Builder + Team cards so both
  // surfaces always render the same variant copy on first cookie-less render.
  const auditCtaExp = useExperiment("audit-cta", {
    tier:    "default",
    source:  embedded ? "contract-page-pricing" : "pricing-audits",
    fallback: { label: "", headline: "", cta: "" },
  });

  const startCheckout = async (sku) => {
    setErrorMsg("");
    if (authLoading) return;
    if (!account) {
      // Embedded variant resumes on /contract; standalone resumes on /pricing/audits.
      const next = embedded
        ? `/contract?sku=${encodeURIComponent(sku)}#pricing`
        : `/pricing/audits?sku=${encodeURIComponent(sku)}`;
      navigate(`/signin?next=${encodeURIComponent(next)}`);
      return;
    }
    setPendingSku(sku);
    try {
      const { data } = await axios.post(
        `${API}/billing/checkout`,
        { plan_id: sku, origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL returned.");
    } catch (e) {
      setPendingSku(null);
      const detail = e?.response?.data?.detail || e?.message || "Checkout failed.";
      setErrorMsg(String(detail));
    }
  };

  // Resume-after-login: if URL has ?sku=... and we're signed in, kick checkout.
  useEffect(() => {
    if (authLoading) return;
    const params = new URLSearchParams(window.location.search);
    const sku = params.get("sku");
    if (sku && account && !pendingSku) {
      const url = new URL(window.location.href);
      url.searchParams.delete("sku");
      window.history.replaceState({}, "", url.toString());
      startCheckout(sku);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, account]);

  return (
    <div id="pricing">
      {/* Hero */}
      <section className={`max-w-[1400px] mx-auto px-6 sm:px-8 ${embedded ? "pt-8" : "pt-16"} pb-8 text-center`}>
        <div className="qt-label inline-block" data-testid="pricing-eyebrow">
          QORTRACE · SMART CONTRACT AUDITS
        </div>
        <h1 className="qt-display text-4xl sm:text-5xl mt-3" style={{ color: "#fff" }}
            data-testid="pricing-headline">
          Audit pricing built for{" "}
          <span style={{ color: "var(--qt-cyan)" }}>builders that move fast</span>.
        </h1>
        <p className="mt-4 max-w-2xl mx-auto" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          Start free, pay-per-audit when you launch a contract, subscribe when you launch them often.
          18 chains supported. Standard tier delivers in &lt;90 seconds; Deep Dive is
          human-reviewed and quoted per engagement.
        </p>

        {/* Billing cycle toggle */}
        <div className="mt-6 inline-flex items-center gap-2 rounded-full p-1"
             style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--qt-border)" }}
             data-testid="pricing-cycle-toggle">
          <button type="button"
                  data-testid="pricing-cycle-monthly"
                  onClick={() => setAnnual(false)}
                  className="qt-mono text-xs px-4 py-1.5 rounded-full transition-colors"
                  style={{
                    background: !annual ? "var(--qt-cyan)" : "transparent",
                    color: !annual ? "#0A1020" : "var(--qt-text-2)",
                    fontWeight: 700, letterSpacing: "0.10em",
                  }}>
            MONTHLY
          </button>
          <button type="button"
                  data-testid="pricing-cycle-annual"
                  onClick={() => setAnnual(true)}
                  className="qt-mono text-xs px-4 py-1.5 rounded-full transition-colors"
                  style={{
                    background: annual ? "var(--qt-cyan)" : "transparent",
                    color: annual ? "#0A1020" : "var(--qt-text-2)",
                    fontWeight: 700, letterSpacing: "0.10em",
                  }}>
            ANNUAL · SAVE 17%
          </button>
        </div>

        {errorMsg && (
          <div className="mt-4 max-w-md mx-auto px-4 py-2 rounded text-sm"
               data-testid="pricing-error"
               style={{
                 background: "rgba(255,110,110,0.1)", color: "#FF8E8E",
                 border: "1px solid rgba(255,110,110,0.3)",
               }}>
            {errorMsg}
          </div>
        )}
      </section>

      {/* Iter267 — PQC Audit Suite bundle configurator. Sits above the
          standard tiers grid so the highest-ticket SKUs are the first
          surface a procurement reader hits. */}
      <div id="pqc-suite" style={{ scrollMarginTop: 80 }}>
        <PqcAuditSuite />
      </div>

      {/* Tiers grid */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
             data-testid="pricing-tiers-grid">
          {TIERS.map((t) => (
            <TierCard key={t.id} tier={t} annual={annual}
                      pendingSku={pendingSku}
                      onCheckout={startCheckout}
                      auditCtaExp={auditCtaExp} />
          ))}
        </div>
      </section>

      {/* Featured audited projects (social proof) */}
      <FeaturedAuditsStrip />

      {/* Deep Dive callout */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-8 py-10">
        <div className="qt-card relative overflow-hidden" data-testid="pricing-deep-dive"
             style={{
               border: "1px solid #FFB000",
               background: "linear-gradient(135deg, rgba(255,176,0,0.08), transparent 60%)",
               padding: "2rem",
             }}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="qt-mono text-xs"
                   style={{ color: "#FFB000", letterSpacing: "0.18em", fontWeight: 700 }}>
                HUMAN-REVIEWED · QUOTED PER ENGAGEMENT
              </div>
              <h2 className="qt-display text-3xl mt-2" style={{ color: "#fff" }}>
                Deep Dive — when the AI's first draft isn't enough.
              </h2>
              <p className="mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                {DEEP_DIVE.blurb}
              </p>
              <ul className="mt-4 space-y-2">
                {DEEP_DIVE.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"
                      style={{ color: "var(--qt-text-2)" }}>
                    <Check size={14} style={{ color: "#FFB000", flexShrink: 0, marginTop: 4 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center lg:text-right">
              <div className="qt-mono text-xs"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em" }}>STARTING AT</div>
              <div className="qt-display text-5xl mt-1" style={{ color: "#FFB000" }}>
                $4,999
              </div>
              <div className="qt-mono text-xs mt-1"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
                per engagement
              </div>
              <Link to="/audit/talk-to-sales"
                    data-testid="pricing-deep-dive-cta"
                    className="qt-btn qt-btn-primary mt-5 inline-flex"
                    style={{ fontSize: 14, padding: "0.75rem 1.25rem" }}>
                Get a quote <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 sm:px-8 py-12">
        <div className="qt-label mb-4">FREQUENTLY ASKED</div>
        <div className="space-y-4">
          <Faq q="What counts as a 'Standard' audit?"
               a="One contract project: a single .sol/.move/.rs file, a folder of contracts under one repo, or a zip of related contracts. We auto-stitch related files so you don't have to submit them one at a time." />
          <Faq q="Do unused monthly audits roll over?"
               a="No — Builder and Team subscriptions reset their audit allowance each month. If you need archived audits, all delivered audits stay in your account permanently. Need a one-off in a slow month? Use a top-up at $149 per audit." />
          <Faq q="What's the difference between Standard and Deep Dive?"
               a="Standard runs the QorTrace audit engine against your code, returns findings + scores in <90s, and auto-delivers. Deep Dive adds a senior human auditor who reviews and edits the engine's findings before delivery (~3-5 business days), plus a threat-model walkthrough call." />
          <Faq q="Can I cancel my subscription anytime?"
               a="Yes. Cancel from the Billing tab and your access ends at the next billing cycle. Audits already delivered to your account stay forever — your certificates keep working, your PDFs keep working, your README badges keep working." />
          <Faq q="Do you sign NDAs?"
               a="Standard tier — no NDA, you keep all rights to your code. Deep Dive — yes, mutual NDA is standard practice and we sign yours or use a Mutual NDA template." />
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-8 py-12">
        <div className="qt-card text-center" data-testid="pricing-final-cta"
             style={{
               border: "1px solid var(--qt-cyan)",
               background: "linear-gradient(135deg, rgba(0,229,255,0.08), transparent 60%)",
               padding: "2.5rem 2rem",
             }}>
          <h2 className="qt-display text-3xl" style={{ color: "#fff" }}>
            Try it on your code right now.
          </h2>
          <p className="mt-3 max-w-xl mx-auto" style={{ color: "var(--qt-text-2)" }}>
            Free preview, no signup. Paste your contract, get findings in 30 seconds.
          </p>
          <Link to="/audit/preview"
                data-testid="pricing-final-cta-btn"
                className="qt-btn qt-btn-primary mt-5 inline-flex"
                style={{ fontSize: 14, padding: "0.75rem 1.5rem" }}>
            <Sparkles size={14} /> Run a free preview <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function TierCard({ tier, annual, pendingSku, onCheckout, auditCtaExp }) {
  const isHighlight = !!tier.highlight;
  const activeSku = tier.ctaKind === "checkout"
    ? (annual && tier.ctaSkuAnnual ? tier.ctaSkuAnnual : tier.ctaSku)
    : null;
  const isPending = !!pendingSku && pendingSku === activeSku;
  const exp = auditCtaExp || { variant: "v1", copy: { cta: "" }, recordClick: () => {} };
  const recordCardClick = () => exp.recordClick && exp.recordClick();

  let displayPrice = tier.price;
  let displayCadence = tier.cadence;
  let secondLine = tier.annual;
  if (annual && tier.annual) {
    if (tier.id === "builder") { displayPrice = "$990"; displayCadence = "/ year"; secondLine = "$82.50 / mo equivalent · save 17%"; }
    if (tier.id === "team")    { displayPrice = "$1,990"; displayCadence = "/ year"; secondLine = "$199 / mo equivalent · save 17%"; }
  }

  return (
    <div
      className="qt-card relative flex flex-col"
      data-testid={`pricing-tier-${tier.id}`}
      style={{
        border: isHighlight ? `2px solid ${tier.accent}` : "1px solid var(--qt-border)",
        background: isHighlight
          ? `linear-gradient(135deg, ${tier.accent}1A, transparent 60%)`
          : "rgba(255,255,255,0.02)",
        padding: "1.75rem 1.5rem",
      }}
    >
      {tier.badge && (
        <div className="qt-mono text-[10px] absolute"
             data-testid={`pricing-tier-${tier.id}-badge`}
             style={{
               top: -10, left: 16,
               background: tier.accent,
               color: "#0A1020",
               padding: "3px 10px",
               borderRadius: 999,
               fontWeight: 800,
               letterSpacing: "0.15em",
             }}>
          {tier.badge}
        </div>
      )}
      <div className="qt-mono text-[10px]"
           style={{ color: tier.accent, letterSpacing: "0.18em", fontWeight: 700 }}>
        {tier.label.toUpperCase()}
      </div>
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <div className="qt-display text-3xl" style={{ color: "#fff" }}>{displayPrice}</div>
        {displayCadence && (
          <div className="qt-mono text-xs"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
            {displayCadence}
          </div>
        )}
      </div>
      {secondLine && (
        <div className="qt-mono text-[10px] mt-1"
             style={{ color: "var(--qt-text-3)", letterSpacing: "0.08em" }}>
          {secondLine}
        </div>
      )}
      <p className="text-xs mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
        {tier.blurb}
      </p>
      <ul className="space-y-1.5 mt-4 mb-5 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs"
              style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
            <Check size={11} style={{ color: tier.accent, flexShrink: 0, marginTop: 4 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {tier.ctaKind === "checkout" ? (
        <button
          type="button"
          onClick={() => { recordCardClick(); onCheckout(activeSku); }}
          disabled={isPending}
          data-testid={`pricing-tier-${tier.id}-cta`}
          data-sku={activeSku}
          data-variant={exp.variant}
          className={isHighlight ? "qt-btn qt-btn-primary" : "qt-btn qt-btn-secondary"}
          style={{
            fontSize: 13, padding: "0.6rem 1rem", justifyContent: "center",
            opacity: isPending ? 0.7 : 1, cursor: isPending ? "wait" : "pointer",
          }}
        >
          {isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Redirecting…
            </span>
          ) : (
            <>{(exp.variant !== "v1" && exp.copy.cta) ? exp.copy.cta : tier.cta} <ArrowRight size={12} /></>
          )}
        </button>
      ) : (
        <Link to={tier.ctaTo}
              data-testid={`pricing-tier-${tier.id}-cta`}
              className={isHighlight ? "qt-btn qt-btn-primary" : "qt-btn qt-btn-secondary"}
              style={{ fontSize: 13, padding: "0.6rem 1rem", justifyContent: "center" }}>
          {tier.cta} <ArrowRight size={12} />
        </Link>
      )}
      <div className="qt-mono text-[10px] text-center mt-2"
           style={{ color: "var(--qt-text-3)" }}>
        {tier.footnote}
      </div>
    </div>
  );
}

// ---- Featured audited projects strip --------------------------------------
function FeaturedAuditsStrip() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/audit/featured?limit=8`);
        if (alive) setAudits(data?.audits || []);
      } catch (_) {
        if (alive) setAudits([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loading && audits.length === 0) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 py-12"
             data-testid="featured-audits-section">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            FEATURED · AUDITED ON QORTRACE
          </div>
          <h2 className="qt-display text-3xl mt-2" style={{ color: "#fff" }}>
            Recently audited, recently verified.
          </h2>
          <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--qt-text-2)" }}>
            Real projects that ran their contracts through QorTrace and earned
            an audit certificate. Click any card to view the public report.
          </p>
        </div>
        <Link to="/audit/preview"
              data-testid="featured-cta"
              className="qt-mono text-xs"
              style={{ color: "var(--qt-cyan)", letterSpacing: "0.10em", fontWeight: 700 }}>
          RUN A FREE PREVIEW →
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => (
            <div key={i} className="qt-card" style={{
              padding: 0, height: 220,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid var(--qt-border)",
            }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
             data-testid="featured-audits-grid">
          {audits.map((a) => <FeaturedCard key={a.id} audit={a} />)}
        </div>
      )}
    </section>
  );
}

function FeaturedCard({ audit }) {
  const sev = audit.severity_summary || {};
  const certUrl = `${process.env.REACT_APP_BACKEND_URL}${audit.certificate_html_url}`;
  const thumbUrl = `${process.env.REACT_APP_BACKEND_URL}${audit.certificate_png_url}`;
  const dateLabel = audit.delivered_at
    ? new Date(audit.delivered_at).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      })
    : "";
  return (
    <a href={certUrl} target="_blank" rel="noreferrer"
       data-testid={`featured-card-${audit.id}`}
       className="qt-card block group"
       style={{
         padding: 0, overflow: "hidden",
         border: "1px solid var(--qt-border)",
         background: "rgba(255,255,255,0.02)",
         transition: "border-color 200ms ease",
       }}
       onMouseEnter={(e) => { e.currentTarget.style.borderColor = audit.score_band_color; }}
       onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--qt-border)"; }}>
      <div style={{ position: "relative", aspectRatio: "3 / 2", background: "#0A1020" }}>
        <img src={thumbUrl}
             alt={`${audit.project_name} audit certificate`}
             loading="lazy"
             draggable={false}
             onContextMenu={(e) => e.preventDefault()}
             style={{ width: "100%", height: "100%", objectFit: "cover",
                      display: "block", pointerEvents: "none",
                      userSelect: "none" }} />
        <div className="qt-mono absolute top-2 right-2 px-2 py-0.5 rounded text-[10px]"
             style={{
               background: audit.score_band_color, color: "#0A1020",
               fontWeight: 800, letterSpacing: "0.10em",
             }}>
          {audit.security_score}/100
        </div>
      </div>
      <div className="p-3">
        <div className="font-bold text-sm truncate" style={{ color: "#fff" }}>
          {audit.project_name}
        </div>
        <div className="qt-mono text-[10px] mt-1 flex items-center gap-2 flex-wrap"
             style={{ color: "var(--qt-text-3)", letterSpacing: "0.08em" }}>
          <span>{(audit.language || "").toUpperCase()}</span>
          <span>·</span>
          <span>{audit.score_band_label}</span>
          {dateLabel && (<><span>·</span><span>{dateLabel.toUpperCase()}</span></>)}
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[10px] qt-mono"
             style={{ color: "var(--qt-text-3)" }}>
          {sev.critical ? <span style={{ color: "#FF6E6E" }}>{sev.critical}C</span> : null}
          {sev.high ? <span style={{ color: "#FF8000" }}>{sev.high}H</span> : null}
          {sev.medium ? <span style={{ color: "#FFB000" }}>{sev.medium}M</span> : null}
          {sev.low ? <span style={{ color: "#1FDD83" }}>{sev.low}L</span> : null}
          {!sev.critical && !sev.high && !sev.medium && !sev.low && (
            <span style={{ color: "#1FDD83" }}>0 ISSUES</span>
          )}
        </div>
      </div>
    </a>
  );
}

function Faq({ q, a }) {
  return (
    <div className="qt-card" style={{ padding: "1.25rem 1.5rem" }}>
      <h3 className="font-bold text-sm" style={{ color: "#fff" }}>{q}</h3>
      <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>{a}</p>
    </div>
  );
}
